/**
 * @description Global Settings → Holidays panel (LYN-103)
 *
 * Key features:
 * - Holiday pay defaults (employee_paid, premium_multiplier) applied on enable/custom
 * - Full-size month calendar of company_holidays
 * - Federal enable list + add custom holiday
 * - Responsive desktop / tablet / mobile layouts
 * - Dirty state integrates with GlobalSettingsPage sticky Discard / Save
 *
 * @dependencies
 * - holidaysService
 * - HolidayMonthGrid
 * - Headless UI Switch / Disclosure (match TimeClockSettingsPanel)
 *
 * @notes
 * - Holidays are API resources (company_holidays), not draft settings keys
 * - Mutations flush on parent sticky Save; Discard restores server snapshot
 * - No HOLPAY/HOLWRK writers (LYN-97); no invented time_clock.holiday_* keys
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CalendarDays,
  ChevronDown,
  Info,
  Plus,
  ShieldCheck,
  X,
} from 'lucide-react';
import { Disclosure, Switch, Transition } from '@headlessui/react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import holidaysService from '../../services/api/holidaysService';
import HolidayMonthGrid from './HolidayMonthGrid';

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const PREMIUM_OPTIONS = [1.0, 1.5, 2.0];

const DEFAULT_PAY = {
  employeePaid: true,
  workedPremium: false,
  premiumMultiplier: 1.0,
};

/**
 * @param {object} props
 * @param {boolean} [props.saving]
 * @param {(dirty: boolean) => void} [props.onDirtyChange]
 * @param {(handlers: { save: () => Promise<void>, discard: () => void }) => void} [props.onRegisterActions]
 * @param {(msg: string|null) => void} [props.onError]
 * @param {(msg: string|null) => void} [props.onSuccess]
 */
const HolidaysSettingsPanel = ({
  saving = false,
  onDirtyChange,
  onRegisterActions,
  onError,
  onSuccess,
}) => {
  const now = new Date();
  const [cursorMonth, setCursorMonth] = useState(
    () => new Date(now.getFullYear(), now.getMonth(), 1)
  );
  const [year, setYear] = useState(now.getFullYear());
  const [loading, setLoading] = useState(true);
  const [platform, setPlatform] = useState([]);
  const [companyHolidays, setCompanyHolidays] = useState([]);
  const [payDefaults, setPayDefaults] = useState(DEFAULT_PAY);
  /** @type {Record<string, { enabled: boolean, employeePaid: boolean, premiumMultiplier: number, companyHolidayId?: string|null }>} */
  const [rowDraft, setRowDraft] = useState({});
  const [baselineRowDraft, setBaselineRowDraft] = useState({});
  const [customDrafts, setCustomDrafts] = useState([]);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customForm, setCustomForm] = useState({
    name: '',
    observedDate: '',
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [localSaving, setLocalSaving] = useState(false);

  const busy = saving || localSaving;

  const effectivePremium = payDefaults.workedPremium
    ? payDefaults.premiumMultiplier
    : 1.0;

  const load = useCallback(async () => {
    setLoading(true);
    onError?.(null);
    try {
      const [platformRes, companyRes] = await Promise.all([
        holidaysService.listPlatformHolidays({ countryCode: 'US', year }),
        holidaysService.listCompanyHolidays({ year, includeInactive: true }),
      ]);

      const platformRows = platformRes.holidays || [];
      const companyRows = companyRes.holidays || [];
      setPlatform(platformRows);
      setCompanyHolidays(companyRows);

      const draft = {};
      platformRows.forEach((ph) => {
        const match = companyRows.find(
          (ch) =>
            ch.platformHolidayId === ph.id &&
            String(ch.observedDate || '').slice(0, 10) ===
              String(ph.observedDate || '').slice(0, 10)
        );
        const enabled = Boolean(match?.isActive);
        const premium = Number(match?.premiumMultiplier ?? 1);
        draft[ph.id] = {
          enabled,
          employeePaid: match ? Boolean(match.employeePaid) : true,
          premiumMultiplier: PREMIUM_OPTIONS.includes(premium) ? premium : 1.0,
          companyHolidayId: match?.id || null,
        };
      });
      setRowDraft(draft);
      setBaselineRowDraft(JSON.parse(JSON.stringify(draft)));
      setCustomDrafts([]);
    } catch (err) {
      console.error('Failed to load holidays', err);
      onError?.(err.response?.data?.error || 'Failed to load holidays');
    } finally {
      setLoading(false);
    }
  }, [year, onError]);

  useEffect(() => {
    load();
  }, [load]);

  // Keep calendar year aligned with cursor month
  useEffect(() => {
    const y = cursorMonth.getFullYear();
    if (y !== year) setYear(y);
  }, [cursorMonth, year]);

  // Pay defaults are a session-local template applied immediately to new
  // enables/custom rows (see queueCustom / updateRow) — they have no
  // dedicated server field of their own, so changing them alone must not
  // flip the panel dirty or enable Save (there is nothing for Save to
  // persist, and `load()` cannot "restore" a server value that never
  // existed). Only row/custom-holiday drafts represent real unsaved writes.
  const isDirty = useMemo(() => {
    if (JSON.stringify(rowDraft) !== JSON.stringify(baselineRowDraft)) {
      return true;
    }
    if (customDrafts.length > 0) return true;
    return false;
  }, [rowDraft, baselineRowDraft, customDrafts]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // Year changes re-run `load()`, which replaces row/custom drafts from the
  // server. If the panel is dirty, confirm before allowing the navigation;
  // cancel keeps the user on the current month so drafts are preserved.
  const handleCursorMonthChange = useCallback(
    (next) => {
      const nextYear = next.getFullYear();
      if (nextYear !== year && isDirty) {
        const ok = window.confirm(
          'You have unsaved holiday changes. Changing the year will discard them. Continue?'
        );
        if (!ok) return;
      }
      setCursorMonth(next);
    },
    [year, isDirty]
  );

  const discard = useCallback(() => {
    setPayDefaults(DEFAULT_PAY);
    setRowDraft(JSON.parse(JSON.stringify(baselineRowDraft)));
    setCustomDrafts([]);
    setShowCustomForm(false);
    setCustomForm({ name: '', observedDate: '' });
  }, [baselineRowDraft]);

  const save = useCallback(async () => {
    setLocalSaving(true);
    onError?.(null);
    try {
      // 1) Federal row enable / disable / pay patches
      for (const ph of platform) {
        const draft = rowDraft[ph.id];
        const base = baselineRowDraft[ph.id];
        if (!draft || !base) continue;

        const changed =
          draft.enabled !== base.enabled ||
          draft.employeePaid !== base.employeePaid ||
          draft.premiumMultiplier !== base.premiumMultiplier;

        if (!changed) continue;

        if (draft.enabled && !base.enabled) {
          await holidaysService.enablePlatformHoliday({
            platformHolidayId: ph.id,
            year,
            employeePaid: draft.employeePaid,
            premiumMultiplier: draft.premiumMultiplier,
          });
        } else if (!draft.enabled && base.enabled && draft.companyHolidayId) {
          await holidaysService.updateCompanyHoliday(draft.companyHolidayId, {
            isActive: false,
          });
        } else if (draft.enabled && draft.companyHolidayId) {
          await holidaysService.updateCompanyHoliday(draft.companyHolidayId, {
            employeePaid: draft.employeePaid,
            premiumMultiplier: draft.premiumMultiplier,
            isActive: true,
          });
        }
      }

      // 2) Pending custom holidays
      for (const custom of customDrafts) {
        await holidaysService.addCustomHoliday({
          name: custom.name,
          observedDate: custom.observedDate,
          countryCode: 'US',
          employeePaid: custom.employeePaid,
          premiumMultiplier: custom.premiumMultiplier,
        });
      }

      await load();
    } catch (err) {
      console.error('Failed to save holidays', err);
      onError?.(err.response?.data?.error || 'Failed to save holidays');
      throw err;
    } finally {
      setLocalSaving(false);
    }
  }, [
    platform,
    rowDraft,
    baselineRowDraft,
    customDrafts,
    year,
    load,
    onError,
  ]);

  useEffect(() => {
    onRegisterActions?.({ save, discard });
  }, [onRegisterActions, save, discard]);

  const updateRow = (platformId, patch) => {
    setRowDraft((prev) => ({
      ...prev,
      [platformId]: { ...prev[platformId], ...patch },
    }));
  };

  const handleEnableAll = (checked) => {
    setRowDraft((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((id) => {
        next[id] = {
          ...next[id],
          enabled: checked,
          employeePaid: checked ? payDefaults.employeePaid : next[id].employeePaid,
          premiumMultiplier: checked
            ? effectivePremium
            : next[id].premiumMultiplier,
        };
      });
      return next;
    });
  };

  const allEnabled =
    platform.length > 0 &&
    platform.every((ph) => rowDraft[ph.id]?.enabled);

  const activeCalendarHolidays = useMemo(() => {
    // Preview: merge server company holidays with draft enables that lack IDs yet
    const byId = new Map();
    companyHolidays
      .filter((h) => h.isActive !== false)
      .forEach((h) => byId.set(h.id, h));

    // Apply draft deactivations
    Object.values(rowDraft).forEach((d) => {
      if (d.companyHolidayId && !d.enabled) {
        byId.delete(d.companyHolidayId);
      }
      if (d.companyHolidayId && d.enabled) {
        const existing = byId.get(d.companyHolidayId);
        if (existing) {
          byId.set(d.companyHolidayId, {
            ...existing,
            employeePaid: d.employeePaid,
            premiumMultiplier: d.premiumMultiplier,
            isActive: true,
          });
        }
      }
    });

    // Draft-only enables (not yet saved)
    platform.forEach((ph) => {
      const d = rowDraft[ph.id];
      if (!d?.enabled || d.companyHolidayId) return;
      const observedDate = ph.observedDate;
      if (!observedDate) return;
      byId.set(`draft-${ph.id}`, {
        id: `draft-${ph.id}`,
        name: ph.name,
        observedDate,
        employeePaid: d.employeePaid,
        premiumMultiplier: d.premiumMultiplier,
        platformHolidayId: ph.id,
        isActive: true,
      });
    });

    customDrafts.forEach((c, i) => {
      byId.set(`custom-draft-${i}`, {
        id: `custom-draft-${i}`,
        name: c.name,
        observedDate: c.observedDate,
        employeePaid: c.employeePaid,
        premiumMultiplier: c.premiumMultiplier,
        isActive: true,
      });
    });

    return Array.from(byId.values());
  }, [companyHolidays, rowDraft, platform, customDrafts]);

  const ModernSwitch = ({ checked, onChange, disabled, label }) => (
    <Switch
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
        checked ? 'bg-brand' : 'bg-gray-200'
      )}
    >
      <span className="sr-only">{label}</span>
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
          checked ? 'translate-x-5' : 'translate-x-0'
        )}
      />
    </Switch>
  );

  const PremiumPills = ({ value, onChange, disabled }) => (
    <div className="flex flex-wrap gap-1.5">
      {PREMIUM_OPTIONS.map((opt) => (
        <button
          key={opt}
          type="button"
          disabled={disabled}
          onClick={() => onChange(opt)}
          className={cn(
            'px-2.5 py-1 rounded-lg text-xs font-bold border transition-colors disabled:opacity-50',
            Number(value) === opt
              ? 'bg-brand-light border-brand text-brand ring-1 ring-brand/20'
              : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
          )}
        >
          {opt.toFixed(1)}×
        </button>
      ))}
    </div>
  );

  const queueCustom = () => {
    if (!customForm.name.trim() || !customForm.observedDate) {
      onError?.('Custom holiday requires a name and date');
      return;
    }
    setCustomDrafts((prev) => [
      ...prev,
      {
        name: customForm.name.trim(),
        observedDate: customForm.observedDate,
        employeePaid: payDefaults.employeePaid,
        premiumMultiplier: effectivePremium,
      },
    ]);
    setCustomForm({ name: '', observedDate: '' });
    setShowCustomForm(false);
    onError?.(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-brand border-t-transparent rounded-full mx-auto mb-3" />
          <p className="text-sm text-gray-500 font-medium">Loading holidays…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24">
      {/* Responsibility callout — match OT / labor rules tone */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3">
        <div className="p-2 bg-blue-100 rounded-lg h-fit">
          <Info className="w-4 h-4 text-blue-600" />
        </div>
        <div>
          <p className="text-sm font-semibold text-blue-900">Your responsibility</p>
          <p className="text-sm text-blue-800 mt-1">
            Consult with legal counsel to decide which holidays and pay premiums apply to your
            business. Observed dates follow the standard weekend rule (Saturday → Friday,
            Sunday → Monday).
          </p>
        </div>
      </div>

      {/* Pay defaults */}
      <Disclosure defaultOpen>
        {({ open }) => (
          <div
            className={cn(
              'bg-white rounded-xl border transition-all duration-200 overflow-hidden shadow-sm',
              open ? 'border-gray-200 ring-1 ring-black/5' : 'border-gray-200'
            )}
          >
            <Disclosure.Button className="w-full flex items-center justify-between p-4 sm:p-5 text-left outline-none">
              <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                <div className="p-2.5 bg-brand-light rounded-xl text-brand shrink-0">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-gray-900">Holiday pay defaults</h3>
                  <p className="text-sm text-gray-500 font-medium truncate">
                    Applied when enabling federal holidays or creating custom ones
                  </p>
                </div>
              </div>
              <ChevronDown
                className={cn(
                  'w-5 h-5 text-gray-400 transition-transform shrink-0',
                  open && 'rotate-180'
                )}
              />
            </Disclosure.Button>
            <Transition
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <Disclosure.Panel className="px-4 sm:px-5 pb-5">
                <div className="h-px bg-gray-100 mb-5" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                  <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 space-y-3">
                    <div>
                      <p className="text-sm font-bold text-gray-900">Holiday pay when off</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Employees receive holiday pay for the day off
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <ModernSwitch
                        checked={payDefaults.employeePaid}
                        disabled={busy}
                        label="Holiday pay when off"
                        onChange={(v) =>
                          setPayDefaults((p) => ({ ...p, employeePaid: v }))
                        }
                      />
                      <span className="text-sm text-gray-600 font-medium">Holiday pay</span>
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 space-y-3">
                    <div>
                      <p className="text-sm font-bold text-gray-900">Premium if they work</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Hours worked on the holiday can include a work premium
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <ModernSwitch
                        checked={payDefaults.workedPremium}
                        disabled={busy}
                        label="Worked premium"
                        onChange={(v) =>
                          setPayDefaults((p) => ({
                            ...p,
                            workedPremium: v,
                            premiumMultiplier: v
                              ? p.premiumMultiplier === 1
                                ? 1.5
                                : p.premiumMultiplier
                              : 1.0,
                          }))
                        }
                      />
                      <span className="text-sm text-gray-600 font-medium">Worked premium</span>
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 space-y-3 md:col-span-2 lg:col-span-1">
                    <div>
                      <p className="text-sm font-bold text-gray-900">Default multiplier</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Pay rate for hours worked on the holiday
                      </p>
                    </div>
                    <PremiumPills
                      value={effectivePremium}
                      disabled={busy || !payDefaults.workedPremium}
                      onChange={(opt) =>
                        setPayDefaults((p) => ({
                          ...p,
                          workedPremium: opt > 1,
                          premiumMultiplier: opt,
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="mt-5 flex items-start sm:items-center gap-3 p-3 rounded-xl border border-gray-100 bg-white">
                  <ModernSwitch
                    checked={allEnabled}
                    disabled={busy || platform.length === 0}
                    label="Enable all US federal"
                    onChange={handleEnableAll}
                  />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      Enable all US federal for this company
                    </p>
                    <p className="text-xs text-gray-500">
                      Turns on all US federal holidays for {year}
                    </p>
                  </div>
                </div>
              </Disclosure.Panel>
            </Transition>
          </div>
        )}
      </Disclosure>

      <HolidayMonthGrid
        cursorMonth={cursorMonth}
        onCursorMonthChange={handleCursorMonthChange}
        holidays={activeCalendarHolidays}
        selectedDate={selectedDate}
        onSelectDay={(dateKey) => setSelectedDate(dateKey)}
      />

      {/* Federal enable list */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 sm:px-5 py-4 border-b border-gray-100 flex items-start gap-3">
          <div className="p-2.5 bg-gray-50 rounded-xl text-gray-600 shrink-0">
            <CalendarDays className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900">Federal observances</h3>
            <p className="text-sm text-gray-500 font-medium">
              Each row can override the defaults above. Turn off Enabled to exclude a holiday
              for your company.
            </p>
          </div>
        </div>

        {/* Desktop / tablet table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-gray-400 border-b border-gray-100">
                <th className="sticky left-0 bg-white z-10 px-5 py-3 font-bold">Holiday</th>
                <th className="px-3 py-3 font-bold whitespace-nowrap">Observed</th>
                <th className="px-3 py-3 font-bold text-center">Enabled</th>
                <th className="px-3 py-3 font-bold text-center">Paid</th>
                <th className="px-3 py-3 font-bold">Work premium</th>
              </tr>
            </thead>
            <tbody>
              {platform.map((ph) => {
                const row = rowDraft[ph.id] || {
                  enabled: false,
                  employeePaid: true,
                  premiumMultiplier: 1.0,
                };
                return (
                  <tr key={ph.id} className="border-b border-gray-50 last:border-0">
                    <td className="sticky left-0 bg-white z-10 px-5 py-3 font-semibold text-gray-900">
                      {ph.name}
                    </td>
                    <td className="px-3 py-3 text-gray-500 whitespace-nowrap font-medium">
                      {ph.observedDate || '—'}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex justify-center">
                        <ModernSwitch
                          checked={row.enabled}
                          disabled={busy}
                          label={`Enable ${ph.name}`}
                          onChange={(v) =>
                            updateRow(ph.id, {
                              enabled: v,
                              employeePaid: v
                                ? payDefaults.employeePaid
                                : row.employeePaid,
                              premiumMultiplier: v
                                ? effectivePremium
                                : row.premiumMultiplier,
                            })
                          }
                        />
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex justify-center">
                        <ModernSwitch
                          checked={row.employeePaid}
                          disabled={busy || !row.enabled}
                          label={`Paid ${ph.name}`}
                          onChange={(v) => updateRow(ph.id, { employeePaid: v })}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <PremiumPills
                        value={row.premiumMultiplier}
                        disabled={busy || !row.enabled}
                        onChange={(opt) =>
                          updateRow(ph.id, { premiumMultiplier: opt })
                        }
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile stacked cards */}
        <div className="md:hidden divide-y divide-gray-100">
          {platform.map((ph) => {
            const row = rowDraft[ph.id] || {
              enabled: false,
              employeePaid: true,
              premiumMultiplier: 1.0,
            };
            return (
              <div key={ph.id} className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-bold text-gray-900 truncate">{ph.name}</p>
                    <p className="text-xs text-gray-500 font-medium mt-0.5">
                      Observed {ph.observedDate || '—'}
                    </p>
                  </div>
                  <ModernSwitch
                    checked={row.enabled}
                    disabled={busy}
                    label={`Enable ${ph.name}`}
                    onChange={(v) =>
                      updateRow(ph.id, {
                        enabled: v,
                        employeePaid: v ? payDefaults.employeePaid : row.employeePaid,
                        premiumMultiplier: v ? effectivePremium : row.premiumMultiplier,
                      })
                    }
                  />
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-gray-600 font-medium">Paid when off</span>
                    <ModernSwitch
                      checked={row.employeePaid}
                      disabled={busy || !row.enabled}
                      label={`Paid ${ph.name}`}
                      onChange={(v) => updateRow(ph.id, { employeePaid: v })}
                    />
                  </div>
                  <div className="space-y-2">
                    <span className="text-sm text-gray-600 font-medium">Work premium</span>
                    <PremiumPills
                      value={row.premiumMultiplier}
                      disabled={busy || !row.enabled}
                      onChange={(opt) =>
                        updateRow(ph.id, { premiumMultiplier: opt })
                      }
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {customDrafts.length > 0 && (
          <div className="border-t border-gray-100 px-4 sm:px-5 py-3 bg-amber-50/40">
            <p className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-2">
              Pending custom holidays
            </p>
            <ul className="space-y-1">
              {customDrafts.map((c, i) => (
                <li
                  key={`${c.name}-${c.observedDate}-${i}`}
                  className="flex items-center justify-between gap-2 text-sm"
                >
                  <span className="font-semibold text-gray-900 truncate">
                    {c.name}{' '}
                    <span className="text-gray-500 font-medium">({c.observedDate})</span>
                  </span>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      setCustomDrafts((prev) => prev.filter((_, idx) => idx !== i))
                    }
                    className="p-1 text-gray-400 hover:text-red-500"
                    aria-label={`Remove ${c.name}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="px-4 sm:px-5 py-4 border-t border-gray-100 flex flex-col sm:flex-row sm:justify-end gap-2">
          {!showCustomForm ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => setShowCustomForm(true)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              Add custom holiday
            </button>
          ) : (
            <div className="w-full grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input
                type="text"
                value={customForm.name}
                onChange={(e) =>
                  setCustomForm((f) => ({ ...f, name: e.target.value }))
                }
                placeholder="Holiday name"
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand focus:border-brand"
                disabled={busy}
              />
              <input
                type="date"
                value={customForm.observedDate}
                onChange={(e) =>
                  setCustomForm((f) => ({ ...f, observedDate: e.target.value }))
                }
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand focus:border-brand"
                disabled={busy}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={queueCustom}
                  className="flex-1 px-3 py-2 bg-brand text-white text-sm font-bold rounded-lg hover:bg-brand-hover disabled:opacity-50"
                >
                  Add
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setShowCustomForm(false);
                    setCustomForm({ name: '', observedDate: '' });
                  }}
                  className="px-3 py-2 text-sm font-semibold text-gray-500 hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HolidaysSettingsPanel;
